import { motion } from 'framer-motion'
import { Zap, CheckCircle, AlertCircle, Play, Pause } from 'lucide-react'

const TaskExecution = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Zap className="mr-3 text-accent-cyan" size={32} />
        任务执行
      </h1>

      {/* 任务模式介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">什么是任务模式</h2>
        <p className="text-gray-400 mb-4">
          任务模式是 Avelia 的核心功能之一，它能够：
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="font-semibold mb-3 text-lg">🎯 自动任务规划</h3>
            <p className="text-sm text-gray-400">
              AI 会将你的需求拆解成多个子任务，制定执行计划，并按步骤完成。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <h3 className="font-semibold mb-3 text-lg">🔧 智能工具调用</h3>
            <p className="text-sm text-gray-400">
              根据任务需要，自动选择合适的工具（文件操作、数据处理、系统控制等）。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <h3 className="font-semibold mb-3 text-lg">📊 实时进度反馈</h3>
            <p className="text-sm text-gray-400">
              在独立的 AI 回复窗口中，实时显示任务列表、执行进度和结果。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <h3 className="font-semibold mb-3 text-lg">🔄 动态任务调整</h3>
            <p className="text-sm text-gray-400">
              执行过程中可以根据实际情况动态添加或调整任务。
            </p>
          </div>
        </div>
      </section>

      {/* 如何使用 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">如何使用任务模式</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h3 className="font-semibold mb-2">切换到任务模式</h3>
              <p className="text-gray-400 text-sm mb-2">
                在聊天窗口右上角，将模式开关切换到"任务模式"（闪电图标）。
              </p>
              <div className="p-3 bg-blue-500/10 rounded text-xs">
                <p className="text-blue-300">💡 提示：任务模式会使用推理模型，确保已配置推理类型的模型。</p>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h3 className="font-semibold mb-2">描述你的需求</h3>
              <p className="text-gray-400 text-sm mb-2">
                清晰地描述你想要完成的任务，AI 会自动规划执行步骤。
              </p>
              <div className="space-y-2 text-sm">
                <div className="p-3 bg-green-500/10 rounded border-l-4 border-green-500">
                  <strong className="text-green-300">示例 1：</strong>
                  <p className="text-gray-400 mt-1">"帮我整理桌面上的文件，按类型分类到不同文件夹"</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded border-l-4 border-green-500">
                  <strong className="text-green-300">示例 2：</strong>
                  <p className="text-gray-400 mt-1">"分析这个 Excel 文件，找出销售额最高的前 10 个产品"</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded border-l-4 border-green-500">
                  <strong className="text-green-300">示例 3：</strong>
                  <p className="text-gray-400 mt-1">"把这个 AVI 视频转换成 MP4 格式，压缩到 50MB 以内"</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h3 className="font-semibold mb-2">查看执行过程</h3>
              <p className="text-gray-400 text-sm mb-2">
                AI 回复窗口会自动弹出，显示：
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• <strong className="text-white">任务列表：</strong>AI 规划的所有子任务</li>
                <li>• <strong className="text-white">执行进度：</strong>当前正在执行的任务</li>
                <li>• <strong className="text-white">工具调用：</strong>使用了哪些工具及其结果</li>
                <li>• <strong className="text-white">思考过程：</strong>AI 的分析和决策逻辑（开启深度思考时）</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              4
            </div>
            <div>
              <h3 className="font-semibold mb-2">获取最终结果</h3>
              <p className="text-gray-400 text-sm mb-2">
                任务完成后，AI 会给出总结和结果说明。如果需要，可以继续提出后续需求。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 任务状态说明 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">任务状态说明</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">状态</th>
                <th className="text-left p-3 font-semibold">图标</th>
                <th className="text-left p-3 font-semibold">说明</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3">等待中</td>
                <td className="p-3"><Pause className="text-gray-400" size={18} /></td>
                <td className="p-3">任务已规划，等待执行</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">执行中</td>
                <td className="p-3"><Play className="text-blue-400" size={18} /></td>
                <td className="p-3">任务正在执行</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">已完成</td>
                <td className="p-3"><CheckCircle className="text-green-400" size={18} /></td>
                <td className="p-3">任务成功完成</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">失败</td>
                <td className="p-3"><AlertCircle className="text-red-400" size={18} /></td>
                <td className="p-3">任务执行失败，查看错误信息</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 高级功能 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">高级功能</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🧠 深度思考模式</h3>
            <p className="text-sm text-gray-400 mb-3">
              在任务模式下，可以开启"深度思考"开关：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>AI 会展示完整的推理过程</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>可以看到 AI 如何分析问题、制定计划</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>适合学习 AI 的思考方式，或处理复杂任务</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">⏸️ 中断任务</h3>
            <p className="text-sm text-gray-400 mb-3">
              如果任务执行时间过长或方向不对，可以随时中断：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>点击聊天窗口的"停止"按钮</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>AI 会安全地停止当前任务</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>已完成的部分会保留</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">📎 附加文件</h3>
            <p className="text-sm text-gray-400 mb-3">
              任务模式支持处理附加的文件：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">Excel：</strong>数据分析、统计、筛选、图表生成</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">Word：</strong>内容提取、文档编辑、摘要生成</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">图片：</strong>OCR 文字识别、图像压缩、格式转换</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">视频：</strong>视频处理、格式转换、截取片段</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 使用建议 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">任务执行建议</h2>
        
        <div className="space-y-3">
          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 明确任务目标</h4>
            <p className="text-sm text-gray-400">
              清楚地说明你想要达到的结果，而不是具体的执行步骤。让 AI 自己规划如何完成。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 提供必要信息</h4>
            <p className="text-sm text-gray-400">
              如果任务涉及特定文件或数据，提前附加文件或说明文件位置。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 合理拆分任务</h4>
            <p className="text-sm text-gray-400">
              特别复杂的任务可以分成几个阶段，逐步完成。每个阶段验证结果后再继续。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 注意权限设置</h4>
            <p className="text-sm text-gray-400">
              某些操作（如文件删除、系统命令）需要在"权限管理"中授权。首次使用时 AI 会提示。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 检查执行结果</h4>
            <p className="text-sm text-gray-400">
              任务完成后，建议检查结果是否符合预期。如有问题，可以要求 AI 重新执行或调整。
            </p>
          </div>
        </div>
      </section>

      {/* 常见任务示例 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-4">💡 常见任务示例</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">文件管理：</strong>
            <p className="text-gray-400 mt-1">"整理下载文件夹，按类型分类文件"</p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">数据处理：</strong>
            <p className="text-gray-400 mt-1">"从这个 Excel 中提取所有邮箱地址"</p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">图像处理：</strong>
            <p className="text-gray-400 mt-1">"压缩这个文件夹里的所有图片，保持质量"</p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">系统控制：</strong>
            <p className="text-gray-400 mt-1">"把电脑音量调到50%，然后打开音乐播放器"</p>
          </div>
        </div>
      </section>
    </motion.div>
  )
}

export default TaskExecution
